// Status model (plan "Status model" + Fable review point 2).
import type { LedgerRow, Recipient } from "@/data/types";
import { isOverLimit } from "./cap";

/** What the UI shows per registration and per phone. */
export type PStatus =
  | "queued"
  | "sending"
  | "sent"
  | "delivered"
  | "read"
  | "retrying"
  /** Meta refused it (131048, 24h limit full); waits for "Retry all" on Overview */
  | "over_limit"
  | "failed"
  | "invalid"
  | "opted_out"
  /** a conversation of this event with no sheet row and no ledger row (e.g. a send-test) */
  | "not_on_list";

export const STATUS_LABEL: Record<PStatus, string> = {
  queued: "Not sent yet",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  retrying: "Retrying",
  over_limit: "Over the limit",
  failed: "Failed",
  invalid: "Invalid number",
  opted_out: "Opted out",
  not_on_list: "Not on the list",
};

export const STATUS_COLOR: Record<PStatus, string> = {
  queued: "var(--st-wait)",
  sending: "var(--st-sent)",
  sent: "var(--st-sent)",
  delivered: "var(--st-deliv)",
  read: "var(--st-read)",
  retrying: "var(--st-retry)",
  over_limit: "var(--warning)",
  failed: "var(--st-bad)",
  invalid: "var(--st-bad)",
  opted_out: "var(--st-wait)",
  not_on_list: "var(--j-off)",
};

/**
 * One registration. `null` = ignore it for status (a `duplicate` ledger row never sends).
 * - no ledger row: invalid if the snapshot says the phone is bad, else queued
 * - failed WITH a wamid: Meta accepted then reported failure; never re-claimed → red
 * - failed WITHOUT a wamid: re-claimed on the next run → amber retrying (also a released over-limit row)
 * - failed with 131048 and not released: over_limit, waits for "Retry all" (checked first: it may have a wamid)
 * - invalid, but the snapshot now has a good phone (fixed in the sheet; record_invalid wrote phone: null and
 *   publish reused the key): queued — the next run re-claims it (isSendable agrees)
 */
export function regStatus(row: LedgerRow | undefined, rec?: Recipient): PStatus | null {
  if (!row) return rec && !rec.phoneOk ? "invalid" : "queued";
  switch (row.status) {
    case "invalid":
      return rec?.phoneOk ? "queued" : "invalid";
    case "failed_final":
      return "failed";
    case "failed":
      if (isOverLimit(row)) return "over_limit";
      return row.wamid ? "failed" : "retrying";
    case "sending":
    case "sent":
    case "delivered":
    case "read":
      return row.status;
    case "duplicate":
      return null;
    default:
      return "queued";
  }
}

const PROGRESS: PStatus[] = ["read", "delivered", "sent", "sending"];

/** Per phone: opt-out wins; then the worst problem (red, then over the limit, then amber); else the furthest
 *  progress; else queued. */
export function phoneStatus(regs: (PStatus | null)[], optedOut: boolean): PStatus {
  if (optedOut) return "opted_out";
  const s = regs.filter((x): x is PStatus => x != null);
  if (s.includes("failed")) return "failed";
  if (s.includes("invalid")) return "invalid";
  if (s.includes("over_limit")) return "over_limit";
  if (s.includes("retrying")) return "retrying";
  for (const p of PROGRESS) if (s.includes(p)) return p;
  return "queued";
}

export const isRed = (s: PStatus) => s === "failed" || s === "invalid";
export const GOT: readonly PStatus[] = ["delivered", "read", "opted_out"];
export const ON_THE_WAY: readonly PStatus[] = ["sending", "sent"];
